import { useState } from 'react'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import DialogTitle from '@mui/joy/DialogTitle'
import DialogContent from '@mui/joy/DialogContent'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import Alert from '@mui/joy/Alert'
import Divider from '@mui/joy/Divider'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import GoogleIcon from '@mui/icons-material/Google'
import WindowIcon from '@mui/icons-material/Window'
import LinkOffIcon from '@mui/icons-material/LinkOff'

import { calendarConnectUrl, disconnectCalendar, type CalendarPlatform, type DsGmeetRepCalendar } from '../../api/dsGmeetApi'
import { useCalendarStatus } from '../../hooks/useCalendarStatus'
import { useCurrentUserId } from '../../hooks/useCurrentUserId'

interface Props {
    open: boolean
    onClose: () => void
}

const statusChipColor: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
    CONNECTED: 'success',
    REVOKED: 'danger',
    DISCONNECTED: 'warning',
}

export function ConnectCalendarModal({ open, onClose }: Props) {
    const userId = useCurrentUserId()
    const { data: cals, loading, refetch } = useCalendarStatus(userId)
    const [pendingId, setPendingId] = useState<string | null>(null)
    const [connectError, setConnectError] = useState('')

    const startConnect = (platform: CalendarPlatform) => {
        if (!userId) return
        setConnectError('')
        const url = calendarConnectUrl(platform, userId)
        const popup = window.open(url, 'calendar_oauth', 'popup,width=600,height=700,left=200,top=100')
        if (!popup) {
            setConnectError('Popup was blocked. Allow popups for this site and try again.')
            return
        }
        const handler = (e: MessageEvent) => {
            if (e.data?.type === 'CALENDAR_AUTH_SUCCESS') {
                window.removeEventListener('message', handler)
                popup.close()
                void refetch()
            } else if (e.data?.type === 'CALENDAR_AUTH_ERROR') {
                window.removeEventListener('message', handler)
                popup.close()
                setConnectError(e.data.message ?? 'Calendar connection failed.')
            }
        }
        window.addEventListener('message', handler)
    }

    const handleDisconnect = async (cal: DsGmeetRepCalendar) => {
        if (!window.confirm(`Disconnect ${cal.account_email}? Future events on this calendar will stop being auto-scheduled.`)) return
        setPendingId(cal.recall_calendar_id)
        try {
            await disconnectCalendar(cal.recall_calendar_id)
            await refetch()
        } finally {
            setPendingId(null)
        }
    }

    const anyRevoked = cals.some((c) => c.status === 'REVOKED')

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ width: 'min(640px, 95vw)' }}>
                <ModalClose />
                <DialogTitle>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <CalendarMonthIcon />
                        Calendar integrations
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2}>
                        <Typography level="body-sm" color="neutral">
                            Connect one or more calendars. Each rep can attach their own — qualifying meetings (external attendees, future, supported video
                            platform) are recorded automatically.
                        </Typography>

                        {cals.length > 0 ? (
                            <Stack gap={1}>
                                {cals.map((cal) => (
                                    <Sheet key={cal.recall_calendar_id} variant="outlined" sx={{ p: 1.5, borderRadius: 'sm' }}>
                                        <Stack direction="row" alignItems="center" gap={1.5} justifyContent="space-between">
                                            <Stack gap={0.5}>
                                                <Typography level="title-sm">{cal.account_email}</Typography>
                                                <Stack direction="row" gap={1} alignItems="center">
                                                    <Chip size="sm" variant="soft" color={statusChipColor[cal.status] ?? 'neutral'}>
                                                        {cal.status}
                                                    </Chip>
                                                    <Typography level="body-xs" color="neutral">
                                                        {cal.platform === 'google_calendar' ? 'Google Calendar' : 'Microsoft Outlook'}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Button
                                                size="sm"
                                                variant="outlined"
                                                color="danger"
                                                startDecorator={<LinkOffIcon />}
                                                loading={pendingId === cal.recall_calendar_id}
                                                onClick={() => void handleDisconnect(cal)}
                                            >
                                                Disconnect
                                            </Button>
                                        </Stack>
                                    </Sheet>
                                ))}
                            </Stack>
                        ) : loading ? (
                            <Typography level="body-sm" color="neutral">
                                Loading…
                            </Typography>
                        ) : (
                            <Typography level="body-sm" color="neutral">
                                No calendar connected. Choose a provider below to start OAuth.
                            </Typography>
                        )}

                        {anyRevoked && <Alert color="warning">A refresh token was revoked. Reconnect that calendar to resume auto-recording.</Alert>}
                        {connectError && (
                            <Alert color="danger" size="sm">
                                {connectError}
                            </Alert>
                        )}

                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <ProviderCard title="Google Calendar" icon={<GoogleIcon />} disabled={!userId} onConnect={() => startConnect('google_calendar')} />
                            <ProviderCard
                                title="Microsoft Outlook"
                                icon={<WindowIcon />}
                                disabled={!userId}
                                onConnect={() => startConnect('microsoft_outlook')}
                            />
                        </Stack>

                        <Divider />

                        <Stack gap={0.5}>
                            <Typography level="title-sm">What gets recorded</Typography>
                            <Typography level="body-xs" color="neutral">
                                • Meetings with at least one external attendee (outside your internal email domains)
                                <br />
                                • Meetings with a supported video URL (Meet / Zoom / Teams / Webex)
                                <br />
                                • Meetings whose organizer or required attendee is on the Sales team
                                <br />• Meetings within the next 14 days whose title does NOT contain <code>[no-record]</code> or <code>numeo:skip</code>
                            </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="flex-end" gap={1}>
                            <Button variant="plain" color="neutral" onClick={() => void refetch()}>
                                Refresh
                            </Button>
                            <Button variant="solid" onClick={onClose}>
                                Done
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </ModalDialog>
        </Modal>
    )
}

function ProviderCard({ title, icon, onConnect, disabled }: { title: string; icon: React.ReactNode; onConnect: () => void; disabled?: boolean }) {
    return (
        <Sheet variant="outlined" sx={{ flex: 1, p: 2, borderRadius: 'sm' }}>
            <Stack gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                    {icon}
                    <Typography level="title-sm">{title}</Typography>
                </Stack>
                <Typography level="body-xs" color="neutral">
                    Authorize Recall to read calendar events.
                </Typography>
                <Button size="sm" variant="solid" onClick={onConnect} disabled={disabled}>
                    Connect
                </Button>
            </Stack>
        </Sheet>
    )
}
